import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaMoneyBillWave, FaMobileAlt } from 'react-icons/fa';
import { MdOutlinePayment } from 'react-icons/md';


const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px 40px;
  box-sizing: border-box; 
  overflow-x: hidden;
  
  @media (max-width: 480px) {
    padding: 0 8px 30px;
    width: 100%;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  color: #947060;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 3px;
    padding: 20px 0;
    margin: 30px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    letter-spacing: 2px;
    padding: 15px 0;
    margin: 20px 0 15px;
  }
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 280px;
    
    @media (max-width: 768px) {
      width: 80%;
      max-width: 280px;
    }
    
    @media (max-width: 480px) {
      width: 90%;
      max-width: 240px;
    }
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 2.4rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 15px;
  color: #b38b6d;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-top: 8px;
  }
`;

const RegistryCard = styled(motion.div)`
  background-color: #fcfaf7;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin: 20px 0;
  position: relative;
  width: 100%;
  max-width: 600px;
  text-align: center;
  box-sizing: border-box; // Add this to include padding in width calculation
  
  @media (max-width: 768px) {
    padding: 25px 15px;
    margin: 15px 0;
    width: 95%;
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
    margin: 10px 0;
    border-radius: 6px;
    width: 100%; // Change to 100% to fill container
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, #d4b59e, #947060);
  }
`;

const RegistryText = styled.p`
  font-family: 'Lato', sans-serif;
  color: #555;
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 20px 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin: 15px 0;
    padding: 0 5px;
  }
`;

const GiftOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 30px 0;
  width: 100%;
  box-sizing: border-box; // Add this
  
  @media (max-width: 768px) {
    margin: 25px 0;
  }
  
  @media (max-width: 480px) {
    margin: 20px 0 15px;
    padding: 0 5px; // Add padding on mobile
  }
`;

const OptionsSectionTitle = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 1.6rem;
  margin-bottom: 25px;
  font-weight: 500;
  position: relative;
  padding-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.4rem;
    margin-bottom: 18px;
    padding-bottom: 8px;
  }
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 100px;
    
    @media (max-width: 480px) {
      width: 80px;
    }
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
  width: 100%;
  margin-top: 15px;
  box-sizing: border-box; // Add this
  
  @media (max-width: 768px) {
    gap: 20px;
  }
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    width: 100%; // Change to 100%
  }
`;

const GiftOptionCard = styled.div`
  background-color: white;
  padding: 25px 20px;
  border-radius: 8px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
  
  /* Cash gift card styling */
  ${props => props.cash && `
    width: 100%;
    max-width: 530px;
    
    @media (max-width: 600px) {
      width: 100%;
      max-width: none;
    }
  `}
  
  /* Venmo and Zelle cards styling */
  ${props => props.venmo && `
    width: 100%;
    min-width: 280px;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 12px rgba(0, 0, 0, 0.1);
    }
    
    @media (max-width: 600px) {
      width: 100%;
      min-width: 250px;
    }
    
    @media (max-width: 480px) {
      min-width: auto;
    }
  `}
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
  
  @media (max-width: 480px) {
    padding: 18px 15px;
  }
`;

const OptionTitle = styled.h3`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.3rem;
    
    @media (max-width: 480px) {
      font-size: 1.2rem;
    }
  }
`;

const OptionText = styled.p`
  font-family: 'Lato', sans-serif;
  color: #666;
  font-size: 1rem;
  line-height: 1.5;
  word-wrap: break-word;
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const VenmoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 15px;
  width: 100%;
  padding: 0 10px;
  
  @media (max-width: 768px) {
    margin-top: 12px;
    padding: 0 8px;
  }
  
  @media (max-width: 480px) {
    margin-top: 10px;
    padding: 0 5px;
  }
`;
const VenmoUsername = styled.p`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.2rem;
  font-weight: 500;
  margin: 10px 0 5px;
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin: 8px 0 4px;
  }
`;

const VenmoNote = styled.p`
  font-family: 'Lato', sans-serif;
  color: #888;
  font-size: 0.9rem;
  margin: 5px 0;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 3px 0;
  }
`;

const ThankYouText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 1.4rem;
  font-style: italic;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-top: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-top: 20px;
  }
`;

const DigitalOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    gap: 30px;
  }
  
  @media (max-width: 480px) {
    gap: 25px;
  }
`;

const PersonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 18px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const PersonTitle = styled.h3`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.3rem;
  font-weight: 500;
  margin-bottom: 10px;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 8px;
  }
`;

const ZelleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 15px;
  width: 100%;
  padding: 0 10px;
  
  @media (max-width: 768px) {
    margin-top: 12px;
    padding: 0 8px;
  }
  
  @media (max-width: 480px) {
    margin-top: 10px;
    padding: 0 5px;
  }
`;


const ZelleInfo = styled.p`
  font-family: 'Lato', sans-serif;
  color: #555;
  font-size: 1.1rem;
  margin: 8px 0;
  font-weight: 500;
  word-break: break-word;
  text-align: center;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin: 6px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    margin: 5px 0;
  }
`;

const PaymentTypeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const PaymentTypeTitle = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  color: #947060;
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 25px;
  text-align: center;
  position: relative;
  padding-bottom: 8px;
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
  
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    width: 80px;
  }
`;

const PeopleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  width: 100%;
  max-width: 600px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 20px;
    max-width: 350px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
    max-width: 320px;
  }
`;

const PersonCard = styled.div`
  background-color: white;
  padding: 30px 25px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-align: center;
  border: 2px solid #f8f9fa;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
    border-color: #d4b59e;
  }
  
  @media (max-width: 768px) {
    padding: 25px 20px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const PersonName = styled.h4`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-bottom: 15px;
  }
`;

const PaymentInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const VenmoProfileImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #d4b59e;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
  
  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
    border: 2px solid #d4b59e;
  }
`;

const ProfileImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ZelleLogo = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  margin-bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  // background: linear-gradient(135deg, #6a1b9a, #8e24aa);
  padding: 10px;
  
  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
    padding: 8px;
  }
`;



const ZelleLogoImg = styled.img`
  width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
  /* Remove the filter that was causing issues */
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PaymentUsername = styled.p`
  font-family: 'Lato', sans-serif;
  color: #947060;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const PaymentDetail = styled.p`
  font-family: 'Lato', sans-serif;
  color: #666;
  font-size: 0.9rem;
  margin: 0;
  word-break: break-word;
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;


const Registry = () => {
  return (
    <PageContainer>
      <PageTitle>
        Gift Registry
        <NameSpan>Erika & Andrew</NameSpan>
      </PageTitle>
      
      <RegistryCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <RegistryText>
          Your presence at our wedding is the greatest gift of all. However, if you wish to honor us with a gift, we would appreciate a cash gift to help us build our new life together.
        </RegistryText>
        
        <GiftOptionsContainer>
          <OptionsSectionTitle>Gift Options</OptionsSectionTitle>
          
          <OptionsContainer>
            {/* Cash gift option - full width */}
            <GiftOptionCard cash>
              <OptionTitle>
                <FaMoneyBillWave /> Cash Gift
              </OptionTitle>
              <OptionText>
                Cash gifts will be gratefully accepted at our reception. A card box will be available at the venue.
              </OptionText>
            </GiftOptionCard>
          </OptionsContainer>
          
          {/* Digital options section title */}
          <OptionsSectionTitle style={{ marginTop: '30px', fontSize: '1.4rem' }}>
            Digital Gift Options
          </OptionsSectionTitle>
          
          {/* Digital payment options redesigned */}
          <DigitalOptionsContainer>
            {/* Venmo Section */}
            <PaymentTypeSection>
              <PaymentTypeTitle>
                <FaMobileAlt style={{ marginRight: '10px', fontSize: '1.3rem' }} />
                Venmo
              </PaymentTypeTitle>
              
              <PeopleGrid>
                <PersonCard>
                  <PersonName>Erika</PersonName>
                  <PaymentInfo>
                    <VenmoProfileImage>
                      <ProfileImg src="/img/erika-yoon-venmo.jpg" alt="Erika's Venmo" />
                    </VenmoProfileImage>
                    <PaymentUsername>@Erika-Yoon</PaymentUsername>
                    <PaymentDetail>Search for username</PaymentDetail>
                  </PaymentInfo>
                </PersonCard>
                
                <PersonCard>
                  <PersonName>Andrew</PersonName>
                  <PaymentInfo>
                    <VenmoProfileImage>
                      <ProfileImg src="/img/andrew-wang-venmo.jpg" alt="Andrew's Venmo" />
                    </VenmoProfileImage>
                    <PaymentUsername>@shamudrew</PaymentUsername>
                    <PaymentDetail>Search for username</PaymentDetail>
                  </PaymentInfo>
                </PersonCard>
              </PeopleGrid>
            </PaymentTypeSection>
            
            {/* Zelle Section */}
            <PaymentTypeSection>
              <PaymentTypeTitle>
                <MdOutlinePayment style={{ marginRight: '10px', fontSize: '1.3rem' }} />
                Zelle
              </PaymentTypeTitle>
              
              <PeopleGrid>
                <PersonCard>
                  <PersonName>Erika</PersonName>
                  <PaymentInfo>
                    <ZelleLogo>
                      <ZelleLogoImg 
                        src="/img/zelle-logo.png" 
                        alt="Zelle Logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{ 
                        display: 'none', 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '1.2rem',
                        fontFamily: 'Lato, sans-serif'
                      }}>
                        Zelle
                      </div>
                    </ZelleLogo>
                    <PaymentUsername>Email</PaymentUsername>
                    <PaymentDetail>erikaleeyoon@gmail.com</PaymentDetail>
                  </PaymentInfo>
                </PersonCard>
                
                <PersonCard>
                  <PersonName>Andrew</PersonName>
                  <PaymentInfo>
                    <ZelleLogo>
                      <ZelleLogoImg 
                        src="/img/zelle-logo.png" 
                        alt="Zelle Logo"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{ 
                        display: 'none', 
                        color: 'white', 
                        fontWeight: 'bold', 
                        fontSize: '1.2rem',
                        fontFamily: 'Lato, sans-serif'
                      }}>
                        Zelle
                      </div>
                    </ZelleLogo>
                    <PaymentUsername>Phone</PaymentUsername>
                    <PaymentDetail>626-476-6755</PaymentDetail>
                  </PaymentInfo>
                </PersonCard>
              </PeopleGrid>
            </PaymentTypeSection>
          </DigitalOptionsContainer>
        </GiftOptionsContainer>
        
        <ThankYouText>Thank you for your love and support!</ThankYouText>
      </RegistryCard>
    </PageContainer>
  );
};

export default Registry;